import { Box, Chip, Container, Divider, Link as MuiLink, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded'
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import HearingRoundedIcon from '@mui/icons-material/HearingRounded'

const AppFooter = () => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'
    const year = new Date().getFullYear()
    const helpMailTo = 'mailto:official@debasishdebnath.in?subject=Happy%20Ears%20App%20Issue'

    const footerNavLinks = [
        { label: 'Dashboard', to: '/' },
        { label: 'Inventory', to: '/inventory' },
        { label: 'Invoice', to: '/generate-invoice' },
        { label: 'Audiometry', to: '/audiometry' },
    ]

    return (
        <Box
            component="footer"
            sx={{
                mt: 'auto',
                width: '100%',
                overflowX: 'clip',
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: isDark
                    ? `0 -8px 22px ${alpha(theme.palette.common.black, 0.3)}`
                    : `0 -8px 18px ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
        >
            <Container
                maxWidth="xl"
                sx={{
                    py: { xs: 1.4, sm: 1.8, md: 2.1 },
                    px: { xs: 1.1, sm: 2, md: 3 },
                    overflowX: 'clip',
                }}
            >
                <Stack spacing={1.2}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={1.25}
                    >
                        <Stack spacing={0.3}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800, letterSpacing: 0.2, color: 'text.primary' }}
                            >
                                Happy Ears Operations Suite
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Secure workflows for audiometry, inventory, invoicing, and patient care.
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={0.8}
                            sx={{
                                width: { xs: '100%', md: 'auto' },
                                flexWrap: 'wrap',
                                rowGap: 0.8,
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                '& .MuiChip-root': {
                                    maxWidth: { xs: '100%', md: 'none' },
                                    borderColor: alpha(theme.palette.primary.main, 0.35),
                                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
                                    color: theme.palette.text.primary,
                                    fontWeight: 600,
                                },
                                '& .MuiChip-label': {
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                },
                            }}
                        >
                            <Chip
                                size="small"
                                icon={<SecurityRoundedIcon style={{ fontSize: 15 }} />}
                                label="Role-based access"
                                variant="filled"
                            />
                            <Chip
                                size="small"
                                icon={<ScheduleRoundedIcon style={{ fontSize: 15 }} />}
                                label="Live operations"
                                variant="filled"
                            />
                        </Stack>
                    </Stack>

                    <Divider sx={{ borderColor: isDark ? alpha('#a6c4ff', 0.25) : alpha('#1f57b8', 0.2) }} />

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={0.8}
                    >
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.6 }}>
                            {[DashboardCustomizeRoundedIcon, Inventory2RoundedIcon, ReceiptLongRoundedIcon, HearingRoundedIcon].map((IconComp, idx) => {
                                const link = footerNavLinks[idx]
                                return (
                                    <Stack key={link.to} direction="row" spacing={0.45} alignItems="center">
                                        <IconComp sx={{ fontSize: 16, color: 'primary.main' }} />
                                        <MuiLink
                                            component={RouterLink}
                                            to={link.to}
                                            underline="hover"
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                '&:hover': { color: 'primary.main' },
                                            }}
                                        >
                                            {link.label}
                                        </MuiLink>
                                    </Stack>
                                )
                            })}
                        </Stack>

                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Support Window: 10:00 AM - 8:00 PM
                        </Typography>
                    </Stack>

                    <Divider sx={{ borderColor: theme.palette.divider }} />

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={0.9}
                    >
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            © {year} Happy Ears Kolkata. All rights reserved.
                        </Typography>
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            <LocalPhoneRoundedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Need help?{' '}
                                <MuiLink
                                    href={helpMailTo}
                                    underline="hover"
                                    sx={{ color: 'primary.main', fontWeight: 700 }}
                                >
                                    Email Support
                                </MuiLink>
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    )
}

export default AppFooter
